import { ExclamationIcon } from "@heroicons/react/outline";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Dataset } from "@/components/ui/icons";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/context/auth-context";
import { useUser } from "@/context/user-context";
import { ToastType } from "@/enums";
import { useCreateDatasetGroup } from "@/queries";
import { displayToast } from "@/utils/toast";

interface CreateDatasetDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess?: () => void;
}

export const CreateDatasetDialog: React.FC<CreateDatasetDialogProps> = ({
  open,
  onOpenChange,
  onSuccess,
}) => {
  const [datasetName, setDatasetName] = useState("");
  const [isCreating, setIsCreating] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const authContext = useAuth();
  const userContext = useUser();
  const navigate = useNavigate();
  const { folderId: folderIdString } = useParams();
  const folderId = folderIdString ? Number(folderIdString) : null;
  const { userToken } = authContext!;
  const workspaceId = userContext.activeWorkspaceId!;

  const createDatasetGroupMutation = useCreateDatasetGroup(userToken!);

  useEffect(() => {
    if (open) {
      setDatasetName("");
      setIsCreating(false);
      setErrorMessage(null);
    }
  }, [open]);

  const handleCreate = async () => {
    if (!datasetName.trim()) {
      setErrorMessage("Please enter a dataset name");
      return;
    }

    setIsCreating(true);
    setErrorMessage(null);

    try {
      const response = await createDatasetGroupMutation.mutateAsync({
        name: datasetName.trim(),
        workspace_id: workspaceId,
        folder_id: folderId || null,
      });

      if (!response.success) {
        throw new Error(response.error || "Error creating dataset");
      }

      const datasetGroupId = response.dataset_group?.id;
      const datasetId = response.dataset?.id;

      if (!datasetGroupId || !datasetId) {
        throw new Error("Invalid response from server");
      }

      displayToast("Dataset created successfully", ToastType.success);

      navigate(`/workspace/${workspaceId}/dataset-groups/${datasetGroupId}/dataset/${datasetId}`);

      onOpenChange(false);
      onSuccess?.();
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error("Error creating dataset:", error);
      const getErrorMessage = (err: unknown): string => {
        if (typeof err === "string") {
          return err;
        }

        if (err instanceof Error) {
          return err.message;
        }

        return "Error creating dataset";
      };

      setErrorMessage(getErrorMessage(error));
    } finally {
      setIsCreating(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleCreate();
    }
  };

  const handleCancel = () => {
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-lg font-medium">
            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-blue-100">
              <Dataset className="h-4 w-4 text-blue-600" />
            </div>
            Create New Dataset
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="dataset-name">Dataset Name</Label>
            <Input
              id="dataset-name"
              type="text"
              value={datasetName}
              onChange={e => setDatasetName(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Enter dataset name..."
              className="w-full"
              // eslint-disable-next-line jsx-a11y/no-autofocus
              autoFocus
              disabled={isCreating}
            />
          </div>

          {folderId && (
            <div className="rounded-md bg-gray-50 p-3">
              <p className="text-sm text-gray-600">
                This dataset will be created in the current folder.
              </p>
            </div>
          )}

          <div className="rounded-md bg-blue-50 p-3">
            <p className="text-sm text-blue-700">
              After creation, you'll be taken to the dataset editor where you can upload data.
            </p>
          </div>

          {errorMessage && (
            <div className="rounded-md bg-red-50 p-3">
              <div className="flex items-start">
                <ExclamationIcon className="h-5 w-5 flex-shrink-0 text-red-400" />
                <div className="ml-2">
                  <p className="text-sm text-red-800">{errorMessage}</p>
                </div>
              </div>
            </div>
          )}
        </div>

        <DialogFooter className="flex justify-between">
          <Button variant="outline" onClick={handleCancel} disabled={isCreating}>
            Cancel
          </Button>
          <Button
            onClick={handleCreate}
            disabled={!datasetName.trim() || isCreating}
            className="min-w-[120px]"
          >
            {isCreating ? (
              <span className="flex items-center gap-2">
                <svg className="h-4 w-4 animate-spin" viewBox="0 0 24 24">
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                    fill="none"
                  />
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  />
                </svg>
                Creating...
              </span>
            ) : (
              "Create Dataset"
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default CreateDatasetDialog;
